import React from 'react';
import {
	Box,
	Heading,
	Text,
	Button,
	Spinner,
	Icon,
	Flex,
	Stack,
	Card,
	CardBody,
	HStack,
	Divider,
	Container,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { MdShoppingBag, MdReceipt } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { clearOrder } from '../../redux/actions/orderActions';
import Navbar from '../../component/navbar';
import Footer from '../../component/footer';
import Alert from '../../component/alert';

const OrderConfirmation = ({ order, orderLoading, clearOrder }) => {
	const navigate = useNavigate();

	const goToStore = () => {
		clearOrder();
		navigate('/store');
	};

	const goToOrders = () => {
		clearOrder();
		navigate('/order');
	};

	if (orderLoading) {
		return (
			<Flex direction="column" minH="100vh" bg="gray.50">
				<Navbar />
				<Alert />
				<Flex flex="1" align="center" justify="center">
					<Box textAlign="center">
						<Spinner size="xl" thickness="4px" color="teal.500" />
						<Text mt={4} color="gray.500">Placing your order...</Text>
					</Box>
				</Flex>
				<Footer />
			</Flex>
		);
	}

	if (!order) {
		return (
			<Flex direction="column" minH="100vh" bg="gray.50">
				<Navbar />
				<Alert />
				<Flex flex="1" align="center" justify="center">
					<Box textAlign="center" py={10} px={6}>
						<Heading size="lg" mb={3} color="gray.700">No order found</Heading>
						<Text mb={6} color="gray.500">Please check your orders or try again.</Text>
						<Button colorScheme="teal" borderRadius="xl" onClick={goToStore}>
							Return to Store
						</Button>
					</Box>
				</Flex>
				<Footer />
			</Flex>
		);
	}

	return (
		<Flex direction="column" minH="100vh" bg="gray.50">
			<Navbar />
			<Alert />
			<Flex flex="1" align="center" justify="center" py={16} px={4}>
				<Container maxW="md">
					{/* Success Icon */}
					<Flex justify="center" mb={6}>
						<Flex
							w="80px" h="80px"
							borderRadius="full"
							bg="green.50"
							align="center"
							justify="center"
						>
							<Icon as={CheckCircleIcon} w={10} h={10} color="green.400" />
						</Flex>
					</Flex>

					<Box textAlign="center" mb={8}>
						<Heading size="xl" color="gray.800" mb={2}>
							Order Confirmed!
						</Heading>
						<Text color="gray.500" fontSize="md">
							Thank you! Your order has been successfully placed and is being processed.
						</Text>
					</Box>

					{/* Order Info Card */}
					<Card borderRadius="2xl" shadow="sm" mb={6}>
						<CardBody>
							<HStack justify="space-between" mb={4}>
								<HStack spacing={2}>
									<Icon as={MdReceipt} color="teal.500" boxSize={5} />
									<Text fontWeight="semibold" color="gray.700">Order Summary</Text>
								</HStack>
								<Text fontSize="sm" color="gray.400">
									#{order?.orderId ?? 'N/A'}
								</Text>
							</HStack>
							<Divider mb={4} />
							<HStack justify="space-between">
								<Text fontSize="sm" color="gray.500">Order ID</Text>
								<Text fontWeight="bold" color="gray.800">#{order?.orderId ?? 'N/A'}</Text>
							</HStack>
							<HStack justify="space-between" mt={2}>
								<Text fontSize="sm" color="gray.500">Status</Text>
								<Text fontWeight="semibold" color="blue.500">Pending</Text>
							</HStack>
						</CardBody>
					</Card>

					{/* Actions */}
					<Stack spacing={3}>
						<Button
							colorScheme="teal"
							size="lg"
							borderRadius="xl"
							leftIcon={<Icon as={MdShoppingBag} />}
							onClick={goToStore}
						>
							Continue Shopping
						</Button>
						<Button
							variant="outline"
							colorScheme="teal"
							size="lg"
							borderRadius="xl"
							leftIcon={<Icon as={MdReceipt} />}
							onClick={goToOrders}
						>
							View My Orders
						</Button>
					</Stack>

					<Text fontSize="xs" color="gray.400" textAlign="center" mt={6}>
						A confirmation has been recorded. Contact support if you have any questions.
					</Text>
				</Container>
			</Flex>
			<Footer />
		</Flex>
	);
};

const mapStateToProps = (state) => ({
	order: state.order.order,
	orderLoading: state.order.loading,
});

export default connect(mapStateToProps, { clearOrder })(OrderConfirmation);
